<div class="status-container min-vh-100" [class.dark-theme]="isDarkTheme" [class.page-busy]="isSaving || isUnsubscribing" [class.save-in-progress]="isSaving">
  @if (viewState === 'loading') {
    <div class="status-init-loader">
      <mat-progress-spinner [diameter]="40" mode="indeterminate"></mat-progress-spinner>
      <p>Loading subscription preferences...</p>
    </div>
  }

  @if (viewState === 'invalid') {
    <div class="status-init-loader">
      <div class="status-message-card invalid">
        <mat-icon class="material-symbols-outlined">link_off</mat-icon>
        <h2>Invalid Link</h2>
        <p>{{ invalidMessage }}</p>
      </div>
    </div>
  }

  @if (viewState === 'error') {
    <div class="status-init-loader">
      <div class="status-message-card error">
        <mat-icon class="material-symbols-outlined">error_outline</mat-icon>
        <h2>Unable to Load Preferences</h2>
        <p>{{ loadErrorMessage }}</p>
        <button mat-stroked-button class="retry-btn" (click)="onRetryLoad()">Retry</button>
      </div>
    </div>
  }

  @if (viewState === 'loaded' && model) {
    <header class="status-topbar">
      <div class="status-shell status-topbar-inner">
        <div class="status-brand-wrap">
          <div class="brand-logo">
            <span>{{ (model?.statusPageName || 'S').charAt(0).toUpperCase() }}</span>
          </div>
          <div class="brand-text">
            <h1>{{ model?.statusPageName || 'Status Page' }}</h1>
            <p>Manage your notification preferences</p>
          </div>
        </div>
        <div class="status-topbar-actions">
          <button
            type="button"
            class="theme-toggle-btn"
            [class.active]="isDarkTheme"
            [disabled]="isSaving || isUnsubscribing"
            [attr.aria-label]="themeToggleLabel"
            [attr.aria-pressed]="isDarkTheme"
            [title]="themeToggleLabel"
            (click)="toggleTheme()">
            <mat-icon class="material-symbols-outlined">{{ isDarkTheme ? 'light_mode' : 'dark_mode' }}</mat-icon>
          </button>
        </div>
      </div>
    </header>

    <main class="status-shell status-main-content">
      <section class="status-section">
        <div class="section-heading">
          <mat-icon class="heading-icon material-symbols-outlined">person</mat-icon>
          <h2>Subscriber Details</h2>
        </div>

        <div class="status-card subscriber-card">
          <div class="subscriber-grid">
            <div class="subscriber-item">
              <span class="item-label">Contact</span>
              <span class="item-value">{{ subscriberContact }}</span>
            </div>
            <div class="subscriber-item">
              <span class="item-label">Channel</span>
              <span class="item-value">{{ subscriberChannelLabel }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="status-section preferences-section">
        <div class="section-heading">
          <mat-icon class="heading-icon material-symbols-outlined">tune</mat-icon>
          <h2>Notification Preferences</h2>
        </div>

        <div class="status-card preferences-card" [class.controls-locked]="isSaving">
          <div class="mode-card">
            <mat-radio-group
              class="mode-options"
              [value]="subscribeToAllServices ? 'all' : 'selected'"
              [disabled]="actionsLocked || model?.isActive === false"
              (change)="onModeChanged($event.value)">
              <mat-radio-button class="mode-option" value="all">
                <span class="mode-option-label">All services</span>
              </mat-radio-button>
              <mat-radio-button class="mode-option" value="selected">
                <span class="mode-option-label">Selected services only</span>
              </mat-radio-button>
            </mat-radio-group>

            @if (subscribeToAllServices) {
              <div class="mode-note">
                You will receive notifications for all {{ totalServiceCount }} services on this status page.
              </div>
            }

            @if (!subscribeToAllServices) {
              <div class="mode-note">
                Selected {{ selectedServiceIds.length }} of {{ totalServiceCount }} services.
              </div>
            }
          </div>

          <div class="services-card" [class.disabled]="selectionLocked">
            @if (showNoServices) {
              <div class="section-empty-state">No services are available for this status page.</div>
            } @else {
              @for (group of groupedServices; track trackByGroup($index, group)) {
                <div class="group-block">
                  <div class="group-header">
                    <mat-checkbox
                      color="primary"
                      [checked]="group.selected"
                      [indeterminate]="group.indeterminate"
                      [disabled]="selectionLocked"
                      (change)="toggleGroup(group, $event.checked)">
                      <span class="group-title">{{ group.groupName }}</span>
                    </mat-checkbox>
                    <span class="group-count">{{ group.selectedCount }}/{{ group.totalCount }}</span>
                  </div>
                  <div class="group-services">
                    @for (service of group.services; track trackByService($index, service)) {
                      <div class="service-row">
                        <mat-checkbox
                          color="primary"
                          [checked]="service.checked"
                          [disabled]="selectionLocked"
                          (change)="toggleService(group, service, $event.checked)">
                          <span class="service-name">{{ service.name }}</span>
                          @if (service.description) {
                            <span class="service-description">{{ service.description }}</span>
                          }
                        </mat-checkbox>
                      </div>
                    }
                  </div>
                </div>
              }
            }
          </div>
        </div>
      </section>

      @if (model?.isActive === false) {
        <div class="inline-alert inline-alert-info" role="status" aria-live="polite">
          <mat-icon class="material-symbols-outlined">info</mat-icon>
          <span>This subscription is inactive. Use the original email link to subscribe again.</span>
        </div>
      }

      @if (selectionMessage) {
        <div class="inline-alert inline-alert-error" role="alert" aria-live="assertive">
          <mat-icon class="material-symbols-outlined">error_outline</mat-icon>
          <span>{{ selectionMessage }}</span>
        </div>
      }

      @if (actionErrorMessage) {
        <div class="inline-alert inline-alert-error" role="alert" aria-live="assertive">
          <mat-icon class="material-symbols-outlined">error_outline</mat-icon>
          <span>{{ actionErrorMessage }}</span>
        </div>
      }

      @if (successMessage) {
        <div class="inline-alert inline-alert-success" role="status" aria-live="polite">
          <mat-icon class="material-symbols-outlined">check_circle</mat-icon>
          <span>{{ successMessage }}</span>
        </div>
      }

      <div class="actions-row">
        <button mat-flat-button color="primary" class="save-btn" [class.saving]="isSaving" [disabled]="saveDisabled" (click)="onSave()">
          @if (isSaving) {
            <mat-progress-spinner [diameter]="16" mode="indeterminate"></mat-progress-spinner>
            <span>Saving...</span>
          } @else {
            <span>Save Preferences</span>
          }
        </button>
        <button mat-stroked-button class="unsubscribe-btn" [disabled]="unsubscribeDisabled" (click)="onUnsubscribe()">
          <mat-icon class="material-symbols-outlined">notifications_off</mat-icon>
          <span>Unsubscribe</span>
        </button>
      </div>

      @if (showUnsubscribeConfirm) {
        <div class="unsubscribe-modal-backdrop" (click)="closeUnsubscribeConfirm()">
          <section class="unsubscribe-modal" role="dialog" aria-modal="true" aria-labelledby="unsubscribe-confirm-title" (click)="$event.stopPropagation()">
            <button type="button" class="unsubscribe-modal-close" aria-label="Close" [disabled]="isUnsubscribing" (click)="closeUnsubscribeConfirm()">
              <mat-icon class="material-symbols-outlined">close</mat-icon>
            </button>

            <div class="unsubscribe-modal-heading">
              <mat-icon class="material-symbols-outlined">warning</mat-icon>
              <h3 id="unsubscribe-confirm-title">Confirm unsubscribe</h3>
            </div>
            <p>This will stop all status notifications for this contact.</p>

            <div class="unsubscribe-modal-context">
              <div class="context-item">
                <span class="context-key">Contact</span>
                <span class="context-val">{{ subscriberContact }}</span>
              </div>
              <div class="context-item">
                <span class="context-key">Status Page</span>
                <span class="context-val">{{ model?.statusPageName || 'Status Page' }}</span>
              </div>
            </div>

            <div class="unsubscribe-modal-actions">
              <button type="button" class="unsubscribe-secondary-btn" [disabled]="isUnsubscribing" (click)="closeUnsubscribeConfirm()">
                Cancel
              </button>
              <button type="button" class="unsubscribe-danger-btn" [disabled]="isUnsubscribing" (click)="confirmUnsubscribe()">
                <span>Unsubscribe</span>
              </button>
            </div>
          </section>
        </div>
      }

      @if (isUnsubscribing) {
        <div class="blocking-loader-overlay" role="status" aria-live="polite" aria-busy="true">
          <mat-progress-spinner [diameter]="42" mode="indeterminate"></mat-progress-spinner>
        </div>
      }
    </main>
  }
</div>
